import { useLocale } from 'next-intl'

import { pushGtmEvent } from '@/src/analytics/gtm'
import { selectClevertapProfile } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getUserAuthValues } from '@/src/utils/auth'
import { getUserSelectedCurrencyCode } from '@/src/utils/currency/getCurrencyCode'
import { getPlatformName } from '@/src/utils/deviceType'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'

interface HolidayContactFormFields {
    formName?: string
    contactName?: string
    contactEmail?: string
    mobileCountryCode?: string
    mobileNumber?: string
    promoCodePresent?: boolean
    emailOptIn?: boolean
    contactAuthorisation?: boolean
}

interface HolidayDetailsPageView {
    packageSlug: string
    collectionSlug: string
    contacted: boolean
}

interface HolidayContactDetailsClick extends HolidayContactFormFields {
    packageSlug: string
    collectionSlug: string
}

const useHolidaysGtmEvent = () => {
    const locale = useLocale()
    const isSignedIn = isUserSignedIn()
    const { email } = getUserAuthValues() || {}
    const clevertapProfile = useAppSelector(selectClevertapProfile)
    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
    const platform = getPlatformName(getDeviceType())?.toLowerCase()

    // Same common attributes as the offers hook, with the holidays product category.
    const commonBase = {
        user_domain: hostname,
        platform,
        user_email: email ? email.toLowerCase() : 'not_informed',
        is_user_logged_in: isSignedIn ? 'yes' : 'no',
        user_language: locale,
        user_currency: getUserSelectedCurrencyCode()?.toUpperCase() || 'not_informed',
        product_category: 'holidays',
        user_phone: clevertapProfile?.Phone || 'not_informed'
    }

    // page_view_holiday_list — /holidays and a holiday's package listing; slug_name = listing slug.
    const pushHolidayListPageViewEvent = (slugName: string) => {
        pushGtmEvent({ event: 'page_view_holiday_list', ...commonBase, slug_name: slugName })
    }

    // page_view_holiday_details — re-pushed with contacted: true once the enquiry succeeds.
    const pushHolidayDetailsPageViewEvent = ({ packageSlug, collectionSlug, contacted }: HolidayDetailsPageView) => {
        pushGtmEvent({
            event: 'page_view_holiday_details',
            ...commonBase,
            slug_name: packageSlug,
            collection_slug: collectionSlug,
            ...(contacted && { contacted: true })
        })
    }

    // click_contact_details — the "Contact me" button click itself, once the enquiry submit succeeds.
    const pushHolidayContactDetailsClickEvent = ({
        packageSlug,
        collectionSlug,
        formName,
        contactName,
        contactEmail,
        mobileCountryCode,
        mobileNumber,
        promoCodePresent,
        emailOptIn,
        contactAuthorisation
    }: HolidayContactDetailsClick) => {
        pushGtmEvent({
            event: 'click_contact_details',
            ...commonBase,
            slug_name: packageSlug,
            collection_slug: collectionSlug,
            form_name: formName,
            contact_name: contactName,
            contact_email: contactEmail,
            mobile_country_code: mobileCountryCode,
            mobile_number: mobileNumber,
            promo_code_present: promoCodePresent,
            email_opt_in: emailOptIn,
            contact_authorisation: contactAuthorisation
        })
    }

    return { pushHolidayListPageViewEvent, pushHolidayDetailsPageViewEvent, pushHolidayContactDetailsClickEvent }
}

export { useHolidaysGtmEvent }
export default useHolidaysGtmEvent
